import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import api from "../lib/api";
import { fmtNumber } from "../lib/utils";
import {
  Target, AlertTriangle, FileText, Sparkles,
  TrendingUp, TrendingDown, ArrowUpRight,
} from "lucide-react";

const ROLE_BLURBS = {
  kpi_owner:    "These are the KPIs you own. Knock the list down to zero this week.",
  esg_lead:     "KPIs assigned to you as ESG Lead — keep the org on the glidepath.",
  tenant_admin: "KPIs you've taken ownership of for this tenant.",
  approver:     "KPIs you've been assigned to oversee.",
  data_contributor: "KPIs you've taken on as a contributor.",
};

/**
 * KPI Owner's "My KPIs" tray for the Dashboard.
 * Renders nothing when the user owns zero KPIs.
 */
export default function MyKpisWidget({ userRole }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get("/my-kpis")
       .then(r => setData(r.data))
       .catch(() => setData({ rows: [], summary: null }));
  }, []);

  if (!data) {
    return (
      <div className="card p-4 text-sm text-ink-soft" data-testid="my-kpis-loading">
        Loading your KPIs…
      </div>
    );
  }
  if (!data.rows || data.rows.length === 0) return null;

  const s = data.summary || {};
  const owner = data.owner || {};
  const blurb = ROLE_BLURBS[userRole] || "These are the KPIs assigned to you.";

  return (
    <div className="card overflow-hidden border-emerald-200/80 ring-1 ring-emerald-100/40"
         data-testid="my-kpis-widget">
      <div className="px-5 py-3 border-b border-emerald-100/80 bg-gradient-to-r from-emerald-50/70 via-emerald-50/30 to-white flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-brand text-white flex items-center justify-center shrink-0">
            <Target size={16}/>
          </div>
          <div className="min-w-0">
            <div className="section-label">My KPIs</div>
            <div className="text-sm font-semibold tracking-tight mt-0.5 truncate">
              {owner.name ? `${owner.name}'s tray` : "Your tray"}
              <span className="text-ink-soft font-normal text-xs ml-2">{blurb}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="my-kpis-summary">
          <Stat label="Owned" value={s.kpis_owned} accent="bg-brand text-white"/>
          <Stat label="On-track" value={`${s.on_track}/${s.kpis_owned}`}
                accent="bg-emerald-100 text-emerald-700"/>
          {s.open_anomalies > 0 && (
            <Stat label="Open anomalies" value={s.open_anomalies}
                  accent={s.anomalies_high > 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}
                  testid="stat-anomalies"/>
          )}
          {s.stale_evidence > 0 && (
            <Stat label="Stale evidence" value={s.stale_evidence}
                  accent="bg-amber-100 text-amber-700" testid="stat-stale"/>
          )}
          <button
            data-testid="my-kpis-toggle"
            onClick={() => setCollapsed(c => !c)}
            className="text-[11px] text-ink-soft hover:text-brand px-2 py-1 rounded"
          >
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4"
             data-testid="my-kpis-grid">
          {data.rows.map(r => <MyKpiCard key={r.kpi_id} r={r}/>)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <span data-testid={testid} className={"pill text-[10px] inline-flex items-center gap-1 " + accent}>
      <span className="opacity-70">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </span>
  );
}

function MyKpiCard({ r }) {
  const direction = r.kpi.direction === "lower_is_better";
  const pct = r.progress != null ? Math.round(r.progress * 100) : null;
  const progressColor =
    pct == null ? "#94A3B8"
    : pct >= 80 ? "#10B981"
    : pct >= 40 ? "#F59E0B"
    : "#EF4444";
  const sparkData = (r.spark || []).map((v, i) => ({ i, v }));

  return (
    <div className="card-soft p-3 hover:shadow-card hover:border-brand/30 transition"
         data-testid={`my-kpi-${r.kpi_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/kpi/${r.kpi_id}`}
                className="font-semibold text-sm truncate block hover:text-brand">
            {r.kpi.name}
          </Link>
          <div className="text-[10px] text-ink-soft mt-0.5">
            {r.kpi.material_issue} · <span className="font-mono">{r.kpi.unit}</span>
          </div>
        </div>
        {r.anomalies_high > 0 && (
          <span className="pill pill-error text-[9px] inline-flex items-center gap-1 shrink-0"
                data-testid={`anom-${r.kpi_id}`}>
            <AlertTriangle size={9}/> {r.anomalies_high} hi
          </span>
        )}
      </div>

      <div className="grid grid-cols-[1fr_64px] gap-2 mt-2 items-end">
        <div className="space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] text-ink-soft">Current</span>
            <span className="font-mono text-sm font-semibold">
              {r.current_value != null ? fmtNumber(r.current_value) : "—"}
              <span className="text-[10px] text-ink-soft ml-1">{r.kpi.unit}</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] text-ink-soft">
              Target{r.target_year ? ` ${r.target_year}` : ""}
              {r.target_custom && (
                <span className="ml-1 text-[8px] text-emerald-700 font-semibold">Custom</span>
              )}
            </span>
            <span className="font-mono text-[11px] text-ink-soft">
              {r.target_value != null ? fmtNumber(r.target_value) : "—"}
            </span>
          </div>
        </div>
        <div className="h-10">
          {sparkData.length > 1 && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`spark-${r.kpi_id}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#064E3B" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#064E3B" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area dataKey="v" stroke="#064E3B" strokeWidth={1.4}
                      fill={`url(#spark-${r.kpi_id})`} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-2">
        <div className="flex items-center justify-between text-[10px] text-ink-soft mb-1">
          <span className="inline-flex items-center gap-1">
            {direction ? <TrendingDown size={9}/> : <TrendingUp size={9}/>}
            Progress to target
          </span>
          <span className="font-mono font-semibold" style={{ color: progressColor }}
                data-testid={`progress-${r.kpi_id}`}>
            {pct != null ? `${pct}%` : "—"}
          </span>
        </div>
        <div className="h-1.5 bg-surface rounded-full overflow-hidden">
          <div className="h-full" style={{
            width: `${Math.min(100, Math.max(0, pct ?? 0))}%`,
            background: progressColor,
          }}/>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-soft border-t border-border pt-2">
        <span>
          {r.current_period
            ? <>Period <span className="font-mono">{r.current_period}</span>
                {r.confidence != null && <span className="text-ink-soft"> · conf {Math.round(r.confidence * 100)}%</span>}
              </>
            : <span className="text-status-error">No measurements yet</span>}
        </span>
        <EvidenceBadge days={r.days_since_evidence}/>
      </div>

      <div className="mt-2 flex gap-1">
        <Link
          data-testid={`submit-${r.kpi_id}`}
          to={`/data-entry?kpi=${r.kpi_id}`}
          className="btn btn-primary text-[11px] flex-1 justify-center"
          title="Submit a new measurement for this KPI"
        >
          <Sparkles size={10}/> Submit measure
        </Link>
        <Link
          data-testid={`view-${r.kpi_id}`}
          to={`/kpi/${r.kpi_id}`}
          className="btn btn-secondary text-[11px]"
          title="Open KPI drill-down"
        >
          <ArrowUpRight size={10}/>
        </Link>
      </div>
    </div>
  );
}

function EvidenceBadge({ days }) {
  if (days == null) {
    return (
      <span className="pill pill-error text-[9px] inline-flex items-center gap-1">
        <FileText size={8}/> No evidence
      </span>
    );
  }
  const cls = days > 60 ? "pill-error" : days > 30 ? "pill-warn" : "pill-success";
  return (
    <span className={"pill text-[9px] inline-flex items-center gap-1 " + cls}>
      <FileText size={8}/>
      {days === 0 ? "today" : `${days}d ago`}
    </span>
  );
}
